import { Theme, css, useTheme } from '@emotion/react';
import { ChangeEvent, HTMLAttributes, forwardRef } from 'react';
import { Controller, FieldError, RegisterOptions, useFormContext } from 'react-hook-form';

import { getValidationValue } from '~src/address/delivery-address-form/utils/hook-form-utils';
import { fontStyles } from '~src/theme/fontStyles';

import { AddressFormFields } from '~address-form/types';

import { InputError } from './input-error';

interface SelectInputV2Props extends HTMLAttributes<HTMLSelectElement> {
  name: keyof AddressFormFields;
  options: { value: string; label: string }[];
  validationRules?: RegisterOptions;
  autoComplete?: string;
  label?: string;
  error?: FieldError;
  onBlur?: () => void;
  dataTest?: string;
  onChange?: (event: ChangeEvent<HTMLSelectElement>) => void;
  value?: string | string[];
  isRequired?: boolean;
  disabled?: boolean;
}

export const SelectInputV2 = forwardRef<HTMLSelectElement, SelectInputV2Props>(
  (
    { name, validationRules, options, label, dataTest, disabled, isRequired = false, ...rest },
    ref
  ) => {
    const theme = useTheme();
    const {
      control,
      formState: { errors },
      resetField,
    } = useFormContext<AddressFormFields>();
    const hasError = Boolean(errors[name]);

    const renderOptionValue = () => {
      if (isRequired) {
        return <option disabled hidden></option>;
      } else {
        return <option value="no-value"></option>;
      }
    };

    return (
      <>
        <Controller
          rules={validationRules}
          name={name}
          control={control}
          render={({ field: { onBlur, onChange, value }, fieldState: { error } }) => (
            <div css={{ position: 'relative' }}>
              <select
                {...rest}
                ref={ref}
                id={`${name}-select-input}`}
                value={value}
                data-test={dataTest}
                css={styles.wrapperStyle(!!error)}
                onChange={value => {
                  if (value.target.value === 'no-value') {
                    resetField(name);
                  } else {
                    onChange(value.target.value);
                  }
                }}
                onBlur={onBlur}
                disabled={disabled}
                style={{
                  color: error ? theme.error35 : theme.neutral35,
                }}
                aria-invalid={hasError}
                aria-required={!!getValidationValue(validationRules?.required)}
              >
                {renderOptionValue()}
                {options.map(({ label, value }) => (
                  <option key={label} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              <label
                htmlFor={`${name}-select-input}`}
                data-test={`${dataTest}-label`}
                style={{
                  color: error ? theme.error35 : theme.neutral35,
                  top: value === '' ? '23px' : '12px',
                  fontSize: value === '' ? '16px' : '12px',
                }}
                css={shellStyle}
              >
                <span>{label}</span>
              </label>
            </div>
          )}
        />
        {hasError && <InputError>{errors[name]?.message}</InputError>}
      </>
    );
  }
);

const styles = {
  wrapperStyle: (error: boolean) => (theme: Theme) =>
    css({
      position: 'relative',
      height: '60px',
      width: '100%',
      padding: '30px 10px 9px 15px',
      borderColor: error ? theme.error35 : theme.neutral85,
      borderRadius: 0, // fix for Safari
      appearance: 'none',
      textOverflow: 'ellipsis',
      overflow: 'hidden !important',
      ':focus-visible': {
        borderColor: error ? theme.error35 : theme.black,
      },
      backgroundColor: 'transparent', // This is needed for iOS Safari
      color: error ? `${theme.error35} !important` : `${theme.black} !important`,
      ...fontStyles.px14_20,
    }),
};

const shellStyle = css({
  position: 'absolute',
  top: '11px',
  left: '16px',
  right: '16px',
  textOverflow: 'ellipsis',
  width: 'calc(100% - 32px)',
  minHeight: '17px',
  height: 'auto',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
  ...fontStyles.px12_16,
});
